import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URLS } from '../../../api/config';
import VideoModal from '../../../components/common/VideoModal';
import './ContentCard.css';

/**
 * 콘텐츠 카드 컴포넌트
 * 콘텐츠 정보를 카드 형태로 표시합니다.
 */
const ContentCard = React.memo(({ content, details }) => {
  // State for video modal
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Use details if available (from ContentDisplay), otherwise fall back to content
  const item = details || content;

  // Format date
  const formattedDate = useMemo(() => {
    const date = item.updated_at || item.created_at;
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, [item]);

  // Determine content type specific elements
  const typeConfig = useMemo(() => {
    const type = item.content_type || 'blog'; // default to blog/article

    switch (type) {
      case 'youtube':
        return {
          icon: (
            <div className="type-icon youtube">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
              </svg>
            </div>
          ),
          label: 'Video'
        };
      case 'external_link':
        return {
          icon: (
            <div className="type-icon link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
              </svg>
            </div>
          ),
          label: 'Link'
        };
      default:
        return {
          icon: null,
          label: 'Article'
        };
    }
  }, [item.content_type]);

  // Render media (Image or Video Thumbnail)
  const renderMedia = useMemo(() => {
    if (item.content_type === 'youtube' && item.youtube_video_id) {
      return (
        <div className="media-container">
          <img
            src={`https://img.youtube.com/vi/${item.youtube_video_id}/maxresdefault.jpg`}
            alt={item.title}
            loading="lazy"
            onError={(e) => {
              // Fallback to hqdefault if maxresdefault is not available
              e.target.src = `https://img.youtube.com/vi/${item.youtube_video_id}/hqdefault.jpg`;
            }}
          />
          <div className="play-overlay">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      );
    }

    if (item.image_path) {
      const imageUrl = (item.image_path.startsWith('http') || item.image_path.startsWith('/'))
        ? item.image_path
        : `${API_URLS.S3}/${item.image_path}`;

      return (
        <div className="media-container">
          <img
            src={imageUrl}
            alt={item.title}
            loading="lazy"
          />
        </div>
      );
    }

    return (
      <div className="media-container no-media">
        <div className="placeholder-pattern"></div>
      </div>
    );
  }, [item]);

  // Get the content ID and type
  const contentId = item.id || content?.id;
  const contentType = item.content_type || content?.content_type || 'blog';

  // Handle click based on type
  const Wrapper = ({ children }) => {
    // YouTube content opens in modal popup
    if (item.content_type === 'youtube' && item.youtube_video_id) {
      return (
        <div
          className="content-card-link youtube-trigger"
          onClick={() => setIsVideoModalOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsVideoModalOpen(true);
            }
          }}
        >
          {children}
        </div>
      );
    }

    // External links open in new tab
    if (item.content_type === 'external_link' && item.external_url) {
      return (
        <a
          href={item.external_url}
          target="_blank"
          rel="noopener noreferrer"
          className="content-card-link"
        >
          {children}
        </a>
      );
    }

    // Default: navigate to content detail page with type and id
    return (
      <Link to={`/content/${contentType}/${contentId}`} className="content-card-link">
        {children}
      </Link>
    );
  };

  return (
    <>
      <div className={`content-card ${item.content_type || 'blog'}`}>
        <Wrapper>
          {renderMedia}
          <div className="card-content">
            <div className="card-meta">
              <span className="card-date">{formattedDate}</span>
              {typeConfig.icon}
            </div>
            <h3 className="card-title">{item.title}</h3>
          </div>
        </Wrapper>
      </div>

      {/* Video Modal for YouTube content */}
      {item.content_type === 'youtube' && item.youtube_video_id && (
        <VideoModal
          videoId={item.youtube_video_id}
          title={item.title}
          isOpen={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
        />
      )}
    </>
  );
});

ContentCard.displayName = 'ContentCard';

export default ContentCard;
